import posthog from "posthog-js";
import type { Me, WorkspaceSummary } from "./api.js";

// Product analytics, session replay, heatmaps and error tracking in PostHog (D-0024). Only a production build sends
// anything, through /ingest: the Pages Function that forwards to PostHog, so blockers that list PostHog's hosts don't
// drop it. The project key is public by design.
const PROJECT_KEY = "phc_n9F5ZnVUxtjZ7hwj3C5ST2sdP34n7qZdv79BZf9cQm2K";

let enabled = false;

function environment(host: string): string {
  if (host.startsWith("app.staging.")) return "staging";
  if (host.startsWith("app.")) return "production";
  return "local";
}

export function initAnalytics() {
  if (!import.meta.env.PROD) return;
  posthog.init(PROJECT_KEY, {
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    defaults: "2026-08-30",
    person_profiles: "identified_only",
    capture_exceptions: true,
    enable_heatmaps: true,
    capture_dead_clicks: true,
    capture_performance: { web_vitals: true, network_timing: true },
    // A claimable connector link carries its token in the query string.
    mask_personal_data_properties: true,
    custom_personal_data_properties: ["token", "code"],
  });
  posthog.register({ environment: environment(location.host) });
  enabled = true;
}

/** The signed-in person: the same id control-api, connector-gateway and core-engine report under. */
export function identify(me: Me) {
  if (!enabled || me.actor.kind !== "user" || posthog.get_distinct_id() === me.actor.id) return;
  posthog.identify(me.actor.id, { email: me.actor.email, name: me.actor.name });
}

/** Signed out, or the session ended: what follows is nobody's. */
export function forget() {
  if (enabled && posthog._isIdentified()) posthog.reset();
}

export function groupWorkspace(workspace: WorkspaceSummary) {
  if (!enabled || posthog.getGroups().workspace === workspace.id) return;
  posthog.group("workspace", workspace.id, { name: workspace.name, slug: workspace.slug });
}

/** An error a route boundary caught: it never reaches window.onerror, so it is reported here. */
export function captureError(err: unknown) {
  if (enabled) posthog.captureException(err);
}
